import React, { useMemo, useState } from 'react';

// (11 Oct 2026, Stage 3: changing an event's date; decisions 89, 90, 97)
// Shown by Edit Event when an upcoming event with a poll timetable moves to a new
// date (the server answers 409 POLL_DATES with the timetable; calendarRoutes.js
// pollTimetable). It lists the poll steps for the new date: steps already passed
// are shown fixed ("✓ Poll phase 1 (offers) opened: Tuesday 29 September"), the
// rest with their new dates. The administrator then:
//   - keeps those dates (only offered when they still fit),
//   - chooses different dates: one drop-down per step still to come, in days
//     before the event with the actual date beside it, or
//   - abandons the poll (decision 89).
// Once poll phase 2 has opened there is also the choice of keeping members'
// confirmations or asking everyone to confirm again (decision 90); changing that
// asks the server for the timetable again (action 'review'), as reopening phase 2
// changes the dates still to come.
// The rules below mirror planErrors in calendarRoutes.js, which checks again on save.
//
// (24 Oct 2026, decision 131) Also opened from an event card for an event that is NOT
// moving (calendarRoutes.js POST .../poll-dates), before any poll exists:
//   mode 'stay'   the poll will open by itself: keep these dates, choose different
//                 ones, or abandon the poll ("{name} is on {date}. Its poll dates:");
//   mode 'resume' the poll was abandoned: let it open by itself again, on the dates
//                 shown or on different ones.
// mode 'move' (the default) is Edit Event's date change, as before.

const MIN_OPEN_DAYS = 3;

const addDays = (iso, n) => {
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const diffDays = (a, b) => Math.round(
  (Date.parse(String(a).slice(0, 10) + 'T00:00:00Z') - Date.parse(String(b).slice(0, 10) + 'T00:00:00Z')) / 86400000);
// (decision 26) Dates in full in questions: "Friday 27 November".
const fullDate = (iso) => new Date(String(iso).slice(0, 10) + 'T00:00:00Z')
  .toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
const daysWords = (n) => (n === 0 ? 'On the event day' : `${n} ${n === 1 ? 'day' : 'days'} before`);

function problemsOf(milestones, newDate, today) {
  const at = {};
  for (const m of milestones) at[m.key] = m;
  const out = [];
  const tomorrow = addDays(today, 1);
  for (const m of milestones) {
    if (m.status !== 'due') continue;
    if (m.date < tomorrow) out.push(`${m.label}: ${fullDate(m.date)} has already passed or is today.`);
    if (m.date > newDate) out.push(`${m.label}: ${fullDate(m.date)} is after the event.`);
  }
  const gap = (a, b) => (at[a] && at[b] ? diffDays(at[b].date, at[a].date) : null);
  if (at.p2Open && at.p2Open.status === 'due' && gap('p1Open', 'p2Open') !== null && gap('p1Open', 'p2Open') < MIN_OPEN_DAYS) {
    out.push(`Poll phase 1 (offers) must stay open at least ${MIN_OPEN_DAYS} days.`);
  }
  if (at.p2Close && at.p2Close.status === 'due' && gap('p2Open', 'p2Close') !== null && gap('p2Open', 'p2Close') < MIN_OPEN_DAYS) {
    out.push(`Poll phase 2 (confirm) must stay open at least ${MIN_OPEN_DAYS} days.`);
  }
  const between = (rem, open, close) => {
    const r = at[rem];
    if (!r || r.status !== 'due') return;
    if ((at[open] && r.date <= at[open].date) || (at[close] && r.date >= at[close].date)) {
      out.push(`${r.label} must fall after the phase opens and before it closes.`);
    }
  };
  between('p1Reminder', 'p1Open', 'p2Open');
  between('p2Reminder', 'p2Open', 'p2Close');
  return out;
}

const PollDatesDialog = ({ timetable, eventTitle, onAnswer, onCancel, mode = 'move' }) => {
  const moving = mode === 'move';
  const resuming = mode === 'resume';
  const { milestones = [], newDate, today, fits, phase2Opened } = timetable || {};
  const clearConfirmations = !!(timetable && timetable.clearConfirmations);
  const dueSteps = milestones.filter((m) => m.status === 'due');
  const maxDays = Math.max(0, diffDays(newDate, addDays(today, 1)));
  const [choice, setChoice] = useState(() => {
    if (resuming) return fits ? 'resume' : 'custom';
    if (!moving) return 'keep'; // decision 131: nothing changes unless chosen
    return dueSteps.length === 0 || fits ? 'keep' : 'custom';
  });
  const [days, setDays] = useState(() => {
    const d = {};
    for (const m of dueSteps) d[m.key] = Math.min(Math.max(0, m.daysBefore), maxDays);
    return d;
  });

  const chosenMilestones = useMemo(() => milestones.map((m) => (m.status === 'due'
    ? { ...m, daysBefore: days[m.key], date: addDays(newDate, -days[m.key]) }
    : m)), [milestones, days, newDate]);
  const problems = choice === 'custom' ? problemsOf(chosenMilestones, newDate, today) : [];
  const shown = choice === 'custom' ? chosenMilestones : milestones;
  const dayOptions = Array.from({ length: maxDays + 1 }, (_, i) => maxDays - i);
  const name = eventTitle && eventTitle.trim() ? `"${eventTitle.trim()}"` : 'This event';

  const save = () => {
    if (resuming) {
      if (choice === 'custom') {
        if (problems.length) return;
        onAnswer({ pollPlan: { action: 'resume', days } });
        return;
      }
      onAnswer({ pollPlan: { action: 'resume' } });
      return;
    }
    if (choice === 'abandon') { onAnswer({ pollPlan: { action: 'abandon' } }); return; }
    if (choice === 'custom') {
      if (problems.length) return; // the problems are listed above the buttons
      onAnswer({ pollPlan: { action: 'custom', days, clearConfirmations } });
      return;
    }
    onAnswer({ pollPlan: { action: 'keep', clearConfirmations } });
  };
  const switchConfirmations = (clear) => {
    if (clear === clearConfirmations) return;
    onAnswer({ pollPlan: { action: 'review', clearConfirmations: clear } });
  };

  const optionClass = (value) => `flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer ${choice === value ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`;
  const confirmClass = (on) => `flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer ${on ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-gray-900 mb-2">Poll dates</h3>
        {moving ? (
          <p className="text-gray-700 mb-3">
            {name} moves to <span className="whitespace-nowrap">{fullDate(newDate)}</span>. Its poll moves with it:
          </p>
        ) : resuming ? (
          <p className="text-gray-700 mb-3">
            {name} is on <span className="whitespace-nowrap">{fullDate(newDate)}</span>. Its poll was abandoned, so no poll will open by itself for it. If it is let open by itself again, its dates are:
          </p>
        ) : (
          <p className="text-gray-700 mb-3">
            {name} is on <span className="whitespace-nowrap">{fullDate(newDate)}</span>. Its poll dates:
          </p>
        )}

        <ul className="space-y-1.5 mb-4 text-sm">
          {shown.map((m) => (
            <li key={m.key} className={m.status === 'passed' ? 'text-gray-500' : 'text-gray-800'}>
              {m.status === 'passed' ? '✓ ' : ''}
              {m.label}: <span className="whitespace-nowrap font-medium">{fullDate(m.date)}</span>
              {choice === 'custom' && m.status === 'due' && (
                <select
                  value={days[m.key]}
                  onChange={(e) => setDays((d) => ({ ...d, [m.key]: Number(e.target.value) }))}
                  className="block mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                  aria-label={`${m.label}: days before the event`}
                >
                  {dayOptions.map((n) => (
                    <option key={n} value={n}>{`${daysWords(n)} — ${fullDate(addDays(newDate, -n))}`}</option>
                  ))}
                </select>
              )}
            </li>
          ))}
        </ul>

        {phase2Opened && (
          <div className="mb-4">
            <p className="text-sm font-semibold text-gray-800 mb-2">Members' confirmations</p>
            <div className="space-y-2">
              <label className={confirmClass(!clearConfirmations)}>
                <input type="radio" className="mt-1" checked={!clearConfirmations} onChange={() => switchConfirmations(false)} />
                <span className="text-sm text-gray-700"><span className="font-medium">Keep members' confirmations.</span> Answers given so far stand for the new date.</span>
              </label>
              <label className={confirmClass(clearConfirmations)}>
                <input type="radio" className="mt-1" checked={clearConfirmations} onChange={() => switchConfirmations(true)} />
                <span className="text-sm text-gray-700"><span className="font-medium">Ask members to confirm again.</span> Their answers stay filled in, marked "not yet confirmed"; poll phase 2 (confirm) opens again for at least {MIN_OPEN_DAYS} days, and you'll be offered the usual message with a request to reconfirm.</span>
              </label>
            </div>
          </div>
        )}

        {moving && !fits && dueSteps.length > 0 && choice !== 'abandon' && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-3">
            These dates no longer fit the new date. Please choose new ones, or abandon the poll.
          </p>
        )}
        {!moving && !fits && dueSteps.length > 0 && choice !== 'abandon' && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-3">
            {resuming
              ? 'Some of these dates have already passed or no longer fit. Please choose different ones.'
              : 'Some of these dates have already passed: poll phase 1 (offers) opens at the next 7 a.m. run unless you choose different dates or abandon the poll.'}
          </p>
        )}

        <div className="space-y-2 mb-4">
          {resuming && fits && (
            <label className={optionClass('resume')}>
              <input type="radio" className="mt-1" checked={choice === 'resume'} onChange={() => setChoice('resume')} />
              <span className="text-sm text-gray-700"><span className="font-medium">Let the poll open by itself again.</span> On the dates shown.</span>
            </label>
          )}
          {!resuming && (moving ? fits : true) && dueSteps.length > 0 && (
            <label className={optionClass('keep')}>
              <input type="radio" className="mt-1" checked={choice === 'keep'} onChange={() => setChoice('keep')} />
              <span className="text-sm text-gray-700"><span className="font-medium">Keep these dates.</span></span>
            </label>
          )}
          {dueSteps.length > 0 && (
            <label className={optionClass('custom')}>
              <input type="radio" className="mt-1" checked={choice === 'custom'} onChange={() => setChoice('custom')} />
              <span className="text-sm text-gray-700"><span className="font-medium">{resuming ? 'Let the poll open by itself again, on different dates.' : 'Choose different dates.'}</span> One choice per step still to come.</span>
            </label>
          )}
          {!resuming && (
          <label className={optionClass('abandon')}>
            <input type="radio" className="mt-1" checked={choice === 'abandon'} onChange={() => setChoice('abandon')} />
            {moving ? (
              <span className="text-sm text-gray-700"><span className="font-medium">Abandon the poll.</span> Its answers are deleted and no poll opens by itself for this event; Open Phase 1 on the event card starts a new one.</span>
            ) : (
              <span className="text-sm text-gray-700"><span className="font-medium">Abandon the poll.</span> No poll opens by itself for this event; Open Phase 1 on the event card starts one whenever you want.</span>
            )}
          </label>
          )}
        </div>

        {choice === 'custom' && problems.length > 0 && (
          <ul className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5 mb-3 space-y-1">
            {problems.map((p) => <li key={p}>{p}</li>)}
          </ul>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
            {moving ? 'Back to the form' : 'Cancel'}
          </button>
          <button type="button" onClick={save} className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">
            {moving ? 'Save the event' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PollDatesDialog;
